import React, { useState, useEffect, useContext, useRef } from "react";
import { Col, Container, Row, Form, Button } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import LeftSideBar from "./LeftSideBar";
import Buttons from "./Buttons";
import RightSideBar from "./RightSideBar";
import Footer from "./Footer";
import SmartUploadWidget from "./smartUploadWidget";
import AddressUploadWidget from "./AddressUploadWidget";
import { Context } from "../context";
import { env } from "../controllers/environment";

const HomePage = ({ className }) => {
  const [showAlert, setShowAlert] = useState(true);
  const [submitStatus, setStatus] = useState(sessionStorage.getItem("submitStatus") !== null);
  const [reference, setReference] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  // Always default to "Bank" on page load/reload
  const getInitialUploadType = () => {
    // Always return "Bank" as the default tab on page load
    // console.log(`Setting default upload type to: Bank`);
    return "Bank";
  };

  const [uploadType, setUploadType] = useState(getInitialUploadType());
  const [widgetKey, setWidgetKey] = useState(0); // For forcing widget remount
  const [diroStatusData, setDiroStatusData] = useState(null); // Store dirostatus response
  const { email } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setInputEmail } = useContext(Context);

  // Ref for the single widget component
  const widgetRef = useRef(null);

  // Check if we're on any smart-upload route
  const isSmartUploadRoute = location.pathname === "/smart-upload" || location.pathname.startsWith("/smart-upload/");

  // Define button IDs for different upload types
  const getButtonId = (type) => {
    switch (type) {
      case "Bank":
        return env.uploadBankBtn; // Bank upload button ID
      case "Address":
        return env.uploadAddressBtn; // Address upload button ID
      default:
        return env.uploadBankBtn;
    }
  };

  // Function to get diro status data from widget
  const updateDiroStatusData = () => {
    if (widgetRef.current) {
      const statusData = widgetRef.current.getDiroStatusResponse();
      if (statusData) {
        setDiroStatusData(statusData);
        // console.log("Updated diro status data:", statusData);
      }
    }
  };

  // Handle tab switching with complete cleanup and reinitialization
  const handleTabSwitch = (newUploadType) => {
    // console.log(`Switching from ${uploadType} to ${newUploadType}`);

    // Only proceed if it's actually a different tab
    if (newUploadType === uploadType) return;

    // Clear previous diro status data
    setDiroStatusData(null);

    // Step 1: Complete widget cleanup
    if (widgetRef.current) {
      // console.log(`Cleaning up ${uploadType} widget completely`);
      widgetRef.current.cleanup();
    }

    // Step 2: Clear any existing widget elements globally
    const existingWidgets = document.querySelectorAll("#reactWidget");
    existingWidgets.forEach((widget) => {
      if (widget.parentNode) {
        widget.parentNode.removeChild(widget);
      }
    });

    // Step 3: Remove existing Diro scripts to force fresh load
    const existingScripts = document.querySelectorAll('script[src*="diro.js"]');
    existingScripts.forEach((script) => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    });

    // Step 4: Clear any Diro global variables
    if (window.DiroWidget) {
      try {
        if (window.DiroWidget.cleanup) {
          window.DiroWidget.cleanup();
        }
        delete window.DiroWidget;
      } catch (e) {
        // console.log("Error clearing DiroWidget:", e.message);
      }
    }

    // Step 5: Save the selected tab to localStorage for persistence
    localStorage.setItem("selectedUploadType", newUploadType);

    // Step 6: Update state to trigger complete component remount
    setUploadType(newUploadType);
    setWidgetKey((prev) => prev + 1); // Force component remount with new key

    // console.log(`Tab switch to ${newUploadType} completed`);
  };

  useEffect(() => {
    const key = sessionStorage.getItem("key");
    if (key === null) {
      setShowAlert(true);
    } else {
      setShowAlert(false);
    }
    const timeoutId = setTimeout(() => {
      sessionStorage.removeItem("key");
    }, 5000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (email) {
      if (
        //eslint-disable-next-line
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          email
        )
      ) {
        localStorage.setItem("reference", email);
        setReference(email);
        setInputEmail && setInputEmail(email);
      } else {
        navigate("/");
      }
    }
  }, [email, navigate, setInputEmail]);

  // Periodically check for diro status data from the widget
  useEffect(() => {
    if (isSmartUploadRoute) {
      const interval = setInterval(() => {
        updateDiroStatusData();
      }, 2000); // Check every 2 seconds

      return () => clearInterval(interval);
    }
  }, [isSmartUploadRoute, uploadType]);

  // Email validation function
  const validateEmail = (email) => {
    //eslint-disable-next-line
    return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      email
    );
  };

  // Handle email submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!emailInput) {
      setEmailError("Email is required");
      return;
    }

    if (!validateEmail(emailInput)) {
      setEmailError("Please enter a valid email");
      return;
    }

    // Navigate to the URL with email parameter
    navigate(`/${emailInput}`);
  };

  // Clear error on input change
  const handleEmailChange = (e) => {
    setEmailInput(e.target.value);
    setEmailError("");
  };

  useEffect(() => {
    if (showAlert) {
      if (submitStatus === false) {
        document.body.style.overflow = "hidden";
      }
    }
  }, [showAlert]);

  function compareStringsWithoutQuotes(str1, str2) {
    const cleanStr1 = str1?.replace(/['"]/g, "");
    const cleanStr2 = str2?.replace(/['"]/g, "");
    return cleanStr1 === cleanStr2;
  }

  useEffect(() => {
    const value = sessionStorage.getItem("event");
    const compareValue = "Terminate session from guac";
    if (showAlert === false && submitStatus === false && compareValue) {
      document.body.style.overflow = "visible";
    }
  }, [showAlert]);

  useEffect(() => {
    submitStatus && setShowAlert(false);
  }, [submitStatus]);

  // Save current tab state when component unmounts or before page unload
  useEffect(() => {
    const saveTabState = () => {
      localStorage.setItem("selectedUploadType", uploadType);
    };

    // Save on beforeunload event (page refresh/close)
    window.addEventListener("beforeunload", saveTabState);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener("beforeunload", saveTabState);
      // Save current state when component unmounts
      saveTabState();
    };
  }, [uploadType]);

  // Initialize widget key when component mounts with default Bank tab
  useEffect(() => {
    // console.log(`Component mounted with default upload type: ${uploadType}`);
    // Ensure localStorage reflects the default Bank selection
    localStorage.setItem("selectedUploadType", "Bank");
    // Trigger widget key update to ensure proper initialization
    setWidgetKey(1);
  }, []); // Empty dependency array - runs only on mount

  const handleClose = () => {
    setShowAlert(false);
    document.body.style.overflow = "visible";
  };

  const alertStyle = {
    width: "340px",
    height: "180px",
    position: "absolute",
    padding: "40px",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#ffa500",
    zIndex: showAlert ? 9999 : -1,
    borderColor: "#ffa500",
  };

  const closeBtnStyle = {
    position: "relative",
    top: "-140px",
    right: "-230px",
    cursor: "pointer",
    fontSize: "2rem",
    border: "0",
    backgroundColor: "transparent",
    color: "black",
  };

  const containerStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    backdropFilter: "blur(5px)",
    zIndex: showAlert ? 9999 : -1,
  };

  return (
    <>
      {showAlert && (
        <div className="alert-container blur-mobile" style={containerStyle}>
          <div className="alert alert-warning al-dismiss alert-dismissible fade show" role="alert" style={alertStyle}>
            <span
              style={{
                textAlign: "justify",
                display: "inline-flex",
                flexDirection: "column",
                justifyContent: "center",
                height: "100%",
                fontWeight: 400,
                color: "black",
              }}
              className="fs-4"
            >
              Please use desktop for an optimized user experience.
            </span>

            <button type="button" style={closeBtnStyle} onClick={handleClose} aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>
      )}
      <Container fluid className={`h-100 ${className}`}>
        <Row className="pt-lg-5 pt-2 mt-0">
          <Col lg={4} md={12} className={`ps-lg-4 pl-sm-2 ${!isSmartUploadRoute ? "pe-lg-5" : ""}`}>
            <LeftSideBar isUploadRoute={isSmartUploadRoute}/>
          </Col>
          <Col lg={isSmartUploadRoute ? 4 : 3} md={12}>
            {isSmartUploadRoute ? (
              <div
                className="d-flex justify-content-md-start justify-content-lg-center align-items-start"
                style={{ margin: "0", padding: "0" }}
              >
                <div
                  style={{
                    // width: "max-content",
                    maxWidth: "400px",
                    margin: "0",
                    height: "max-content",
                    background: "white",
                    borderRadius: "10px",
                    textAlign: "center",
                    position: "relative",
                    overflow: "hidden",
                    // paddingBottom: "15px",
                    boxShadow: "rgba(67, 71, 85, 0.27) 0px 0px 0.25em, rgba(90, 125, 188, 0.05) 0px 0.25em 1em",
                    minHeight: "300px",
                  }}
                >
                  <div
                    style={{
                      // width: "auto",
                      minWidth: "380px",
                      maxWidth: "500px",
                      margin: "0",
                      height: "max-content",
                      background: "white",
                      borderRadius: "10px",
                      textAlign: "center",
                      position: "relative",
                      overflow: "hidden",
                      paddingBottom: "15px",
                      // padding:"10px"
                    }}
                  >
                    {/* Upload Type Tabs - Only Bank Verification enabled */}
                    <div
                      style={{
                        display: "flex",
                        width: "100%",
                        margin: "0",
                        backgroundColor: "#fff",
                        gap: "8px",
                        justifyContent: "center",
                        // marginTop: "15px",
                      }}
                    >
                      {/* <div
                        style={{
                          maxWidth: "220px",
                          padding: "6px 22px",
                          borderRadius: "8px",
                          backgroundColor: "#FF4F00",
                          color: "#FFFFFF",
                          fontSize: "14px",
                          fontWeight: "600",
                          boxShadow: "0 1px 3px rgba(0, 122, 255, 0.3)",
                        }}
                      >
                        Bank verification
                      </div> */}

                      {/* Address verification commented out as per request
                      <button
                        onClick={() => handleTabSwitch("Address")}
                        style={{
                          maxWidth: "220px",
                          padding: "6px 22px",
                          border: "none",
                          borderRadius: "8px",
                          backgroundColor: uploadType === "Address" ? "#FF4F00" : "#F2F2F7",
                          color: uploadType === "Address" ? "#FFFFFF" : "#8E8E93",
                          fontSize: "14px",
                          fontWeight: uploadType === "Address" ? "600" : "500",
                          cursor: "pointer",
                          // transition: "all 0.2s ease",
                          boxShadow: uploadType === "Address" ? "0 1px 3px rgba(0, 122, 255, 0.3)" : "none",
                          // transform: uploadType === "Address" ? "scale(1.01)" : "scale(1)",
                        }}
                        onMouseEnter={(e) => {
                          if (uploadType !== "Address") {
                            e.target.style.backgroundColor = "#E5E5EA";
                            e.target.style.color = "#1D1D1F";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (uploadType !== "Address") {
                            e.target.style.backgroundColor = "#F2F2F7";
                            e.target.style.color = "#8E8E93";
                          }
                        }}
                      >
                        Address verification
                      </button>
                      */}
                    </div>

                    <div
                      style={{
                        padding: "0px 20px 0px 20px",
                        margin: "0",
                        // borderRadius: "0 0 10px 10px",
                        overflow: "hidden",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        position: "relative",
                      }}
                    >
                      {/* Widget container - always present to prevent blinking */}
                      <div style={{ width: "100%", height: "auto", position: "relative" }}>
                        <SmartUploadWidget
                          key={`widget-${uploadType}-${widgetKey}`}
                          ref={widgetRef}
                          urn={email || ""}
                          trackId={email || reference || emailInput || ""}
                          buttonId={getButtonId(uploadType)}
                          wrapperConfig={{
                            height: "auto",
                            width: "300px",
                            themeColor: "rgb(255, 79, 0)",
                            fontFamily: "inherit",
                            fontSizeTitle: "14px",
                            fontWeightTitle: "600",
                            buttonHeight: "40px",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : reference === "" ? (
              <div
                className="ms-lg-5 d-flex h-100 align-items-start w-100 ps-5 pe-5 ps-md-2 ms-lg-4"
                style={{ marginTop: "0", paddingTop: "0" }}
              >
                <div
                  style={{
                    width: "100%",
                    maxWidth: "500px",
                    margin: "0",
                    padding: "2rem",
                    background: "white",
                    borderRadius: "10px",
                    textAlign: "center",
                  }}
                >
                  <Form onSubmit={handleSubmit}>
                    <div className="mt-lg-4 mt-3 mb-lg-0 mb-4" style={{ display: "block", height: "2rem", width: "15rem" }}>
                      <div className="form input-group" style={{ marginLeft: "0.5rem", marginTop: "2.5rem" }}>
                        <Form.Control
                          type="email"
                          placeholder="Email"
                          aria-label="Refernce ID"
                          aria-describedby="referenceId"
                          value={emailInput}
                          onChange={handleEmailChange}
                          className="small-input border-2 border-end-0 p-2 fs-6 emailbox form-control"
                          style={{
                            border: emailError ? "1px solid red" : "1px solid rgb(255, 79, 0)",
                          }}
                        />
                        <div className="border-start-0 rounded-end-3" style={{ border: "2px solid rgb(255, 79, 0)" }}>
                          <div style={{ display: "block" }}>
                            <button
                              type="submit"
                              className="btn btn-primary rounded-3 btn-md text-white input-group-text"
                              id="referenceId"
                              style={{
                                padding: "8px 12px",
                                transform: "translate(4%, 0%)",
                                border: "1px solid rgb(255, 79, 0)",
                                backgroundColor: "rgb(255, 79, 0)",
                              }}
                            >
                              Go
                            </button>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "inline-block" }}></div>
                    </div>
                    {emailError && (
                      <div style={{ color: "red", textAlign: "left", fontSize: "0.85rem", marginTop: "0.5rem" }}>
                        {emailError}
                      </div>
                    )}
                  </Form>
                </div>
              </div>
            ) : (
              <Buttons reference={reference} />
            )}
          </Col>
          <Col
            lg={4}
            md={12}
            className={`${!isSmartUploadRoute ? "pl-lg-5" : ""}`}
            style={{ marginLeft: isSmartUploadRoute ? "0" : "auto" }}
          >
            <RightSideBar isUploadRoute={isSmartUploadRoute} diroStatusData={diroStatusData?.btnDetails} />
          </Col>
        </Row>
        {!showAlert && <Footer />}
      </Container>
    </>
  );
};

export default HomePage;
